export function shouldSkipRouteQuickFindPreseed({
  focusedCapsuleId = "",
  hiddenCapsuleId = "",
} = {}) {
  return Boolean(
    String(focusedCapsuleId || "").trim() ||
      String(hiddenCapsuleId || "").trim()
  );
}

function cleanId(value = "") {
  return String(value || "").trim();
}

function normalizeCapsuleList(capsules = []) {
  const byId = new Map();
  (Array.isArray(capsules) ? capsules : []).forEach((capsule) => {
    if (!capsule || typeof capsule !== "object") return;
    const id = cleanId(capsule.id || capsule.capsuleId);
    if (!id) return;
    byId.set(id, { ...capsule, id });
  });
  return Array.from(byId.values());
}

function capsuleIdsFromCapsules(capsules = []) {
  return normalizeCapsuleList(capsules).map((capsule) => capsule.id);
}

function normalizeCategoryKey(value = "") {
  return String(value || "").trim().toLowerCase();
}

const QUICK_FIND_FUN_SEED_TYPES = new Set(["see", "do", "relax", "shop", "fun"]);

const QUICK_FIND_TYPE_PATTERNS = {
  drink: /\b(bar|pub|brewery|winery|distillery|night[\s_-]?club|cocktail|coffee|cafe|tea|juice|lounge)\b/,
  eat: /\b(restaurant|food|meal[\s_-]?takeaway|meal[\s_-]?delivery|bakery|pizza|sandwich|deli|diner|grill|taco|burger|ramen|sushi)\b/,
  fun: /\b(museum|tourist[\s_-]?attraction|art[\s_-]?gallery|park|amusement|aquarium|zoo|bowling|movie[\s_-]?theater|stadium|casino|spa|wellness|landmark|entertainment|experience|activity|shopping[\s_-]?mall)\b/,
};

function normalizeTypeToken(value = "") {
  return String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase();
}

function collectCapsuleCategorySignals(capsule = {}) {
  const placeFacts = capsule?.placeFacts && typeof capsule.placeFacts === "object"
    ? capsule.placeFacts
    : {};
  const rawTypes = [
    capsule?.category,
    capsule?.primaryCategory,
    capsule?.seedCategory,
    ...(Array.isArray(capsule?.types) ? capsule.types : []),
    ...(Array.isArray(capsule?.placeTypes) ? capsule.placeTypes : []),
    ...(Array.isArray(capsule?.googleTypes) ? capsule.googleTypes : []),
    ...(Array.isArray(placeFacts?.types) ? placeFacts.types : []),
    placeFacts?.category,
    placeFacts?.primaryCategory,
  ];

  return rawTypes.map(normalizeTypeToken).filter(Boolean).join(" ");
}

function inferQuickFindCategory(capsule = {}) {
  const seedType = normalizeCategoryKey(capsule?.seedType);
  if (seedType === "eat" || seedType === "drink") return seedType;
  if (QUICK_FIND_FUN_SEED_TYPES.has(seedType)) return "fun";

  const signals = collectCapsuleCategorySignals(capsule);
  if (!signals) return "";

  if (QUICK_FIND_TYPE_PATTERNS.drink.test(signals)) return "drink";
  if (QUICK_FIND_TYPE_PATTERNS.eat.test(signals)) return "eat";
  if (QUICK_FIND_TYPE_PATTERNS.fun.test(signals)) return "fun";
  return "";
}

function replaceCacheCategory(cacheKey = "", categoryKey = "") {
  const normalizedCategoryKey = normalizeCategoryKey(categoryKey);
  const text = String(cacheKey || "").trim();
  if (!text || !normalizedCategoryKey) return "";
  const lastColon = text.lastIndexOf(":");
  if (lastColon < 0) return "";
  return `${text.slice(0, lastColon + 1)}${normalizedCategoryKey}`;
}

function buildIdleCategoryState() {
  return { phase: "idle", count: 0, capsuleIds: [] };
}

function isFreshCacheEntry(entry, { now = Date.now(), ttlMs } = {}) {
  const cachedAt = Number(entry?.at || 0);
  const maxAge = Number(ttlMs);
  return Boolean(cachedAt && Number.isFinite(maxAge) && Number(now) - cachedAt <= maxAge);
}

function freshCapsuleIdsFromEntry(entry, { now = Date.now(), ttlMs } = {}) {
  if (!isFreshCacheEntry(entry, { now, ttlMs })) {
    return [];
  }
  const storedIds = Array.isArray(entry?.capsuleIds) ? entry.capsuleIds : [];
  const storedCapsuleIds = capsuleIdsFromCapsules(entry?.capsules);
  return Array.from(
    new Set(
      [...storedIds, ...storedCapsuleIds]
        .map(cleanId)
        .filter(Boolean)
    )
  );
}

function freshCapsulesFromEntry(entry, { now = Date.now(), ttlMs } = {}) {
  if (!isFreshCacheEntry(entry, { now, ttlMs })) {
    return [];
  }
  return normalizeCapsuleList(entry?.capsules);
}

export function buildQuickFindCategoryCacheEntries({
  baseCacheKey = "",
  baseEntry = {},
  capsules = [],
  categoryKeys = ["eat", "drink", "fun"],
} = {}) {
  const cleanBaseCacheKey = String(baseCacheKey || "").trim();
  if (!cleanBaseCacheKey || !baseEntry || typeof baseEntry !== "object") return {};

  return (Array.isArray(categoryKeys) ? categoryKeys : [])
    .map(normalizeCategoryKey)
    .filter(Boolean)
    .reduce((entries, categoryKey) => {
      const categoryCapsules = normalizeCapsuleList(
        (Array.isArray(capsules) ? capsules : [])
          .filter((capsule) => inferQuickFindCategory(capsule) === categoryKey)
      );
      const capsuleIds = categoryCapsules.map((capsule) => capsule.id);
      if (!capsuleIds.length) return entries;
      const categoryCacheKey = replaceCacheCategory(cleanBaseCacheKey, categoryKey);
      if (!categoryCacheKey) return entries;
      entries[categoryCacheKey] = {
        ...baseEntry,
        capsules: categoryCapsules,
        capsuleIds,
        count: capsuleIds.length,
        sourceCacheKey: cleanBaseCacheKey,
      };
      return entries;
    }, {});
}

export function buildQuickFindDirectCategoryCacheEntry({
  baseCacheKey = "",
  baseEntry = {},
  categoryKey = "",
  capsules = [],
} = {}) {
  const cleanBaseCacheKey = String(baseCacheKey || "").trim();
  const normalizedCategoryKey = normalizeCategoryKey(categoryKey);
  const categoryCacheKey = replaceCacheCategory(cleanBaseCacheKey, normalizedCategoryKey);
  if (!categoryCacheKey || !baseEntry || typeof baseEntry !== "object") {
    return null;
  }

  const normalizedCapsules = normalizeCapsuleList(capsules);
  const capsuleIds = normalizedCapsules.map((capsule) => capsule.id);

  return {
    cacheKey: categoryCacheKey,
    entry: {
      ...baseEntry,
      categoryKey: normalizedCategoryKey,
      capsules: normalizedCapsules,
      capsuleIds,
      count: capsuleIds.length,
      sourceCacheKey: cleanBaseCacheKey,
    },
  };
}

export function hasFreshQuickFindCategoryCacheSet({
  cache = {},
  combinedCacheKey = "",
  categoryKeys = ["eat", "drink", "fun"],
  now = Date.now(),
  ttlMs,
} = {}) {
  const cleanCombinedCacheKey = String(combinedCacheKey || "").trim();
  if (!cleanCombinedCacheKey) return false;

  return (Array.isArray(categoryKeys) ? categoryKeys : [])
    .map(normalizeCategoryKey)
    .filter(Boolean)
    .every((categoryKey) => {
      const categoryCacheKey = replaceCacheCategory(cleanCombinedCacheKey, categoryKey);
      return Boolean(categoryCacheKey && isFreshCacheEntry(cache?.[categoryCacheKey], { now, ttlMs }));
    });
}

export function buildQuickFindCategoryStatesFromCache({
  cache = {},
  combinedCacheKey = "",
  categoryKeys = ["eat", "drink", "fun"],
  loading = false,
  now = Date.now(),
  ttlMs,
  minReadyCount = 1,
} = {}) {
  const cleanCombinedCacheKey = String(combinedCacheKey || "").trim();
  const isLoading = Boolean(loading);
  const minCount = Math.max(1, Number(minReadyCount) || 1);

  return (Array.isArray(categoryKeys) ? categoryKeys : ["eat", "drink", "fun"])
    .map(normalizeCategoryKey)
    .filter(Boolean)
    .reduce((states, categoryKey) => {
      const categoryCacheKey = replaceCacheCategory(cleanCombinedCacheKey, categoryKey);
      const capsuleIds = freshCapsuleIdsFromEntry(cache?.[categoryCacheKey], {
        now,
        ttlMs,
      });
      if (capsuleIds.length >= minCount) {
        states[categoryKey] = {
          phase: "ready",
          count: capsuleIds.length,
          capsuleIds,
        };
        return states;
      }

      states[categoryKey] = isLoading
        ? { phase: "loading", count: 0, capsuleIds: [] }
        : buildIdleCategoryState();
      return states;
    }, {});
}

export function resolveQuickFindCachedCapsuleIds({
  cache = {},
  categoryCacheKey = "",
  combinedCacheKey = "",
  now = Date.now(),
  allowCombinedFallback = false,
  ttlMs,
} = {}) {
  const categoryIds = freshCapsuleIdsFromEntry(cache?.[categoryCacheKey], {
    now,
    ttlMs,
  });
  if (categoryIds.length) return categoryIds;
  if (!allowCombinedFallback) return [];

  return freshCapsuleIdsFromEntry(cache?.[combinedCacheKey], {
    now,
    ttlMs,
  });
}

export function resolveQuickFindCachedCapsules({
  cache = {},
  categoryCacheKey = "",
  combinedCacheKey = "",
  now = Date.now(),
  allowCombinedFallback = false,
  ttlMs,
} = {}) {
  const categoryCapsules = freshCapsulesFromEntry(cache?.[categoryCacheKey], {
    now,
    ttlMs,
  });
  if (categoryCapsules.length) return categoryCapsules;
  if (!allowCombinedFallback) return [];

  return freshCapsulesFromEntry(cache?.[combinedCacheKey], {
    now,
    ttlMs,
  });
}

function toFiniteLatLng(center) {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function distanceMeters(a, b) {
  const start = toFiniteLatLng(a);
  const end = toFiniteLatLng(b);
  if (!start || !end) return Infinity;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusMeters = 6371000;
  const deltaLat = toRadians(end.lat - start.lat);
  const deltaLng = toRadians(end.lng - start.lng);
  const startLat = toRadians(start.lat);
  const endLat = toRadians(end.lat);
  const h =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(deltaLng / 2) ** 2;
  return 2 * earthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(h)));
}

function inferCenterFromCacheKey(cacheKey) {
  const match = String(cacheKey || "").match(/^geo:([-+]?\d+(?:\.\d+)?),([-+]?\d+(?:\.\d+)?):/);
  if (!match) return null;
  return toFiniteLatLng({ lat: match[1], lng: match[2] });
}

export function findFreshQuickFindPreseedNearCenter(cache = {}, {
  center,
  categoryKey = "",
  now = Date.now(),
  ttlMs,
  radiusMeters,
} = {}) {
  const activeCenter = toFiniteLatLng(center);
  const normalizedCategoryKey = String(categoryKey || "").trim().toLowerCase();
  const maxAge = Number(ttlMs);
  const radius = Number(radiusMeters);
  if (!activeCenter || !normalizedCategoryKey || !Number.isFinite(maxAge) || !Number.isFinite(radius)) {
    return null;
  }

  return Object.entries(cache || {}).reduce((best, [cacheKey, entry]) => {
    if (!String(cacheKey || "").endsWith(`:${normalizedCategoryKey}`)) return best;
    const cachedAt = Number(entry?.at || 0);
    if (!cachedAt || Number(now) - cachedAt > maxAge) return best;
    const cachedCenter = toFiniteLatLng(entry?.center) || inferCenterFromCacheKey(cacheKey);
    if (!cachedCenter) return best;
    const distance = distanceMeters(activeCenter, cachedCenter);
    if (distance > radius) return best;
    if (best && best.distanceMeters <= distance) return best;
    return {
      cacheKey,
      entry,
      center: cachedCenter,
      distanceMeters: distance,
    };
  }, null);
}

export function findQuickFindPreseedInFlightNearCenter(inFlightEntries = [], {
  center,
  categoryKey = "",
  radiusMeters,
} = {}) {
  const activeCenter = toFiniteLatLng(center);
  const normalizedCategoryKey = String(categoryKey || "").trim().toLowerCase();
  const radius = Number(radiusMeters);
  if (!activeCenter || !normalizedCategoryKey || !Number.isFinite(radius)) {
    return null;
  }

  const entries = inFlightEntries instanceof Map
    ? Array.from(inFlightEntries.values())
    : Array.isArray(inFlightEntries)
      ? inFlightEntries
      : Object.values(inFlightEntries || {});

  return entries.reduce((best, entry) => {
    const entryCategoryKey = String(entry?.categoryKey || "").trim().toLowerCase();
    if (entryCategoryKey !== normalizedCategoryKey) return best;
    const entryCenter = toFiniteLatLng(entry?.center);
    if (!entryCenter) return best;
    const distance = distanceMeters(activeCenter, entryCenter);
    if (distance > radius) return best;
    if (best && best.distanceMeters <= distance) return best;
    return {
      ...entry,
      center: entryCenter,
      distanceMeters: distance,
    };
  }, null);
}
